import type { GameEnvironment, Player } from "../game";
import type { HexState } from "./types";

const NEIGHBORS = [[-1, 0], [-1, 1], [0, -1], [0, 1], [1, -1], [1, 0]] as const;

function connected(board: Int8Array, size: number, player: Player): boolean {
  const seen = new Uint8Array(size * size);
  const pending: number[] = [];
  for (let i = 0; i < size; i += 1) {
    const action = player === 0 ? i : i * size;
    if (board[action] === player) pending.push(action);
  }
  while (pending.length) {
    const action = pending.pop()!;
    if (seen[action]) continue;
    seen[action] = 1;
    const row = Math.floor(action / size);
    const column = action % size;
    if ((player === 0 && row === size - 1) || (player === 1 && column === size - 1)) {
      return true;
    }
    for (const [dr, dc] of NEIGHBORS) {
      const r = row + dr;
      const c = column + dc;
      const next = r * size + c;
      if (r >= 0 && r < size && c >= 0 && c < size && !seen[next] && board[next] === player) {
        pending.push(next);
      }
    }
  }
  return false;
}

export class PgxHex implements GameEnvironment<HexState> {
  constructor(readonly size: number) {
    if (!Number.isInteger(size) || size < 3 || size > 11) throw new RangeError("Hex size must be 3–11");
  }

  init(): HexState {
    return {
      size: this.size,
      currentPlayer: 0,
      board: new Int8Array(this.size * this.size).fill(-1),
      stepCount: 0,
      moves: [],
      lastAction: -1,
      winner: null,
      terminated: false,
      rewards: [0, 0],
    };
  }

  step(state: HexState, action: number): HexState {
    if (state.terminated || !Number.isInteger(action) || action < 0 ||
        action >= this.size * this.size || state.board[action] !== -1) {
      const rewards: [number, number] = [0, 0];
      rewards[state.currentPlayer] = -1;
      rewards[(1 - state.currentPlayer) as Player] = 1;
      return { ...state, terminated: true, winner: (1 - state.currentPlayer) as Player, rewards };
    }
    const board = state.board.slice();
    board[action] = state.currentPlayer;
    const won = connected(board, this.size, state.currentPlayer);
    const rewards: [number, number] = [0, 0];
    if (won) {
      rewards[state.currentPlayer] = 1;
      rewards[(1 - state.currentPlayer) as Player] = -1;
    }
    return {
      ...state,
      board,
      currentPlayer: (1 - state.currentPlayer) as Player,
      stepCount: state.stepCount + 1,
      moves: [...state.moves, { action, label: this.formatAction(state, action) }],
      lastAction: action,
      winner: won ? state.currentPlayer : null,
      terminated: won,
      rewards,
    };
  }

  observe(state: HexState): Float32Array {
    const output = new Float32Array(this.size * this.size * 4);
    for (let i = 0; i < state.board.length; i += 1) {
      output[i * 4] = state.board[i] === state.currentPlayer ? 1 : 0;
      output[i * 4 + 1] = state.board[i] === 1 - state.currentPlayer ? 1 : 0;
      // PGX no-swap retains the color and on-board mask planes.
      output[i * 4 + 2] = 1 - state.currentPlayer;
      output[i * 4 + 3] = 1;
    }
    return output;
  }

  legalActionMask(state: HexState): Uint8Array {
    const mask = new Uint8Array(this.size * this.size);
    if (state.terminated) return mask.fill(1);
    for (let i = 0; i < mask.length; i += 1) mask[i] = state.board[i] === -1 ? 1 : 0;
    return mask;
  }

  currentPlayer(state: HexState): Player { return state.currentPlayer; }
  isTerminal(state: HexState): boolean { return state.terminated; }
  terminalValue(state: HexState): number { return state.rewards[state.currentPlayer] ?? 0; }
  formatAction(_state: HexState, action: number): string {
    return `${String.fromCharCode(65 + (action % this.size))}${Math.floor(action / this.size) + 1}`;
  }
}
